import {
  Brain,
  CheckCircle2,
  ChevronDown,
  Clock,
  FileText,
  Sparkles,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { MarkdownText } from '@/components/common/MarkdownText';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Subheading } from '@/components/ui/catalyst/heading';
import { Text } from '@/components/ui/catalyst/text';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import {
  useGetAIExtractionQuery,
  useGetMedicalProvidersQuery,
  useGetTreatmentCheckInsQuery,
  useGetTreatmentCheckInTaskUpdatesQuery,
} from '@/services/api/treatmentService';
import {
  MedicalProvider,
  ProviderTreatmentStatus,
  TreatmentCheckInProviderUpdate,
  TreatmentCheckInStatus,
} from '@/services/types/treatment-types';

import { treatmentStatusInfo } from '../constants';
import { useActionableSummary } from './hooks';
import { CheckInSummary, useCheckInDeltas } from './useCheckInDeltas';

const EMPTY_REVIEW_PROVIDERS: never[] = [];

const useGetCompletedCheckIns = (matterId: string) => {
  const { data: checkIns, isLoading: isLoadingCheckIn } =
    useGetTreatmentCheckInsQuery(matterId);

  const { data: taskUpdates, isLoading: isLoadingTaskUpdates } =
    useGetTreatmentCheckInTaskUpdatesQuery({
      matterId,
    });

  const { data: providers, isLoading: isLoadingProviders } =
    useGetMedicalProvidersQuery(matterId);

  const mapProviderUpdateToCheckInSummary = useCallback(
    (
      pu: TreatmentCheckInProviderUpdate,
      pr: MedicalProvider[],
    ): CheckInSummary['provider_updates'][number] => {
      const provider = pr.find((p) => p.id === pu.provider_id);
      const providerName = provider?.name ?? pu.provider_name ?? '';
      return {
        ...pu,
        provider_name: providerName,
        new_treatment_notes: pu.new_treatment_notes,
        new_treatment_status: pu.new_treatment_status,
      };
    },
    [],
  );

  const combinedCheckIns = useMemo(() => {
    if (
      taskUpdates === undefined ||
      checkIns === undefined ||
      providers === undefined
    ) {
      return [];
    }

    const checkInsFromTaskUpdates: CheckInSummary[] = taskUpdates.map((tu) => ({
      check_in_time: tu.created_at,
      general_notes: tu.update,
      provider_updates: [],
    }));

    const checkInsFromCheckIns: CheckInSummary[] = checkIns
      .filter(
        (ci) =>
          ci.status === TreatmentCheckInStatus.COMPLETED &&
          ci.check_in_time !== null,
      )
      .map((ci) => ({
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        check_in_time: ci.check_in_time!,
        general_notes: ci.general_notes,
        provider_updates:
          ci.provider_updates?.map((pu) =>
            mapProviderUpdateToCheckInSummary(pu, providers),
          ) ?? [],
      }));

    return [...checkInsFromTaskUpdates, ...checkInsFromCheckIns].sort(
      (a, b) =>
        new Date(b.check_in_time).getTime() -
        new Date(a.check_in_time).getTime(),
    );
  }, [checkIns, mapProviderUpdateToCheckInSummary, providers, taskUpdates]);

  const isLoading =
    isLoadingCheckIn || isLoadingTaskUpdates || isLoadingProviders;

  return { combinedCheckIns, isLoading };
};

export const LastCheckInOverview = ({
  matterId,
  structuredObjectivesEnabled = false,
}: {
  matterId: string;
  structuredObjectivesEnabled?: boolean;
}) => {
  const { combinedCheckIns, isLoading } = useGetCompletedCheckIns(matterId);
  const lastCheckIn = combinedCheckIns[0] ?? null;
  const deltas = useCheckInDeltas(combinedCheckIns);

  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isCompletedOpen, setIsCompletedOpen] = useState(false);

  // Get actual check-ins to find the ID for AI extraction
  const { data: checkIns } = useGetTreatmentCheckInsQuery(matterId);

  // Sort check-ins by check_in_time descending to find the most recent
  const sortedCheckIns = useMemo(() => {
    if (!checkIns) return [];
    return [...checkIns]
      .filter(
        (ci) =>
          ci.status === TreatmentCheckInStatus.COMPLETED &&
          ci.check_in_time !== null,
      )
      .sort(
        (a, b) =>
          new Date(b.check_in_time!).getTime() -
          new Date(a.check_in_time!).getTime(),
      );
  }, [checkIns]);

  const lastCompletedCheckIn = sortedCheckIns[0];
  const secondCompletedCheckIn = sortedCheckIns[1];

  // Fetch AI extraction for the two most recent completed check-ins.
  // The latest check-in may not have an extraction (e.g. manual-only check-in),
  // so we fall back to the previous one.
  const { data: aiExtractionFirst, isLoading: isLoadingAIFirst } =
    useGetAIExtractionQuery(
      { matterId, checkInId: lastCompletedCheckIn?.id ?? '' },
      { skip: !lastCompletedCheckIn?.id },
    );
  const { data: aiExtractionSecond, isLoading: isLoadingAISecond } =
    useGetAIExtractionQuery(
      { matterId, checkInId: secondCompletedCheckIn?.id ?? '' },
      { skip: !secondCompletedCheckIn?.id },
    );

  // Use the first completed extraction we find
  const aiExtraction =
    aiExtractionFirst?.extraction_status === 'completed'
      ? aiExtractionFirst
      : aiExtractionSecond?.extraction_status === 'completed'
        ? aiExtractionSecond
        : undefined;
  const isLoadingAI = isLoadingAIFirst || isLoadingAISecond;

  // Get actionable summary (skip legacy objectives fetch when structured objectives are on)
  const actionableSummary = useActionableSummary(
    matterId,
    structuredObjectivesEnabled ? undefined : lastCompletedCheckIn?.id,
    aiExtraction,
    EMPTY_REVIEW_PROVIDERS, // Review found providers now handled in EditCheckInForm
  );

  if (isLoading) {
    return (
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md animate-pulse">
        <Text className="text-xs text-blue-700 flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" />
          Loading last check-in...
        </Text>
      </div>
    );
  }

  if (!lastCheckIn) {
    return (
      <div className="p-3 bg-blue-50 border border-blue-200 rounded-md">
        <Text className="text-xs text-blue-700 flex items-center gap-1.5">
          <Clock className="h-3.5 w-3.5" />
          No previous check-ins found
        </Text>
      </div>
    );
  }

  // Split provider deltas into completed and active
  const completedProviderDeltas =
    deltas[0]?.providerDeltas.filter(
      (pd) =>
        pd.update.new_treatment_status === ProviderTreatmentStatus.COMPLETED,
    ) ?? [];
  const activeProviderDeltas =
    deltas[0]?.providerDeltas.filter(
      (pd) =>
        pd.update.new_treatment_status !== ProviderTreatmentStatus.COMPLETED,
    ) ?? [];

  return (
    <div className="space-y-2">
      {/* 1. ACTIONABLE SUMMARY - Blue, Prominent, Compact (hidden when structured objectives enabled) */}
      {!structuredObjectivesEnabled && (
        <div className="p-3 bg-blue-50 border-2 border-blue-300 rounded-lg shadow-sm">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Sparkles className="h-4 w-4 text-blue-600" />
              <Subheading level={2} className="text-blue-900 text-sm">
                Objectives for This Check-in
              </Subheading>
            </div>
          </div>

          {isLoadingAI ? (
            <div className="flex items-center gap-2 text-xs text-blue-700">
              <Clock className="h-3.5 w-3.5 animate-spin" />
              <span>Generating objectives...</span>
            </div>
          ) : actionableSummary.summaryParts.length > 0 ? (
            <ul className="space-y-1">
              {actionableSummary.summaryParts.map((item, idx) => (
                <li
                  key={idx}
                  className="flex items-start gap-2 text-xs text-blue-800"
                >
                  <span className="text-blue-600 mt-0.5">•</span>
                  <span className="flex-1">{item}</span>
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-xs text-blue-700">
              No specific action items identified. Continue with regular check-in
              questions.
            </p>
          )}
        </div>
      )}

      {/* 2. Side-by-side collapsible sections */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {/* 2a. PREVIOUS CHECK-IN NOTES */}
        <Collapsible
          open={isDetailsOpen}
          onOpenChange={(open) => {
            setIsDetailsOpen(open);
            setIsCompletedOpen(open);
          }}
        >
          <div className="p-2 border border-gray-300 rounded-lg bg-white">
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="w-full flex items-center justify-between hover:bg-gray-50 p-1.5"
              >
                <div className="flex items-center gap-2">
                  <FileText className="h-3.5 w-3.5 text-gray-600" />
                  <span className="text-xs font-medium text-gray-900">
                    Last Check-in Notes
                  </span>
                </div>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-gray-600 transition-transform ${
                    isDetailsOpen ? 'rotate-180' : ''
                  }`}
                />
              </Button>
            </CollapsibleTrigger>

            <CollapsibleContent className="pt-2 space-y-2">
              <div className="px-2 text-[11px] text-gray-500">
                {lastCheckIn.check_in_time
                  ? new Date(lastCheckIn.check_in_time).toLocaleDateString()
                  : 'Date unknown'}
              </div>

              {/* Source Transcript Link */}
              {lastCompletedCheckIn?.source_transcript_id && (
                <div className="px-2 py-1.5 bg-gray-50 border border-gray-200 rounded text-xs">
                  <div className="flex items-center justify-between">
                    <span className="text-gray-800 font-medium">
                      Source Transcript
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={!lastCompletedCheckIn.source_conversation_id}
                      onClick={() =>
                        lastCompletedCheckIn.source_conversation_id &&
                        window.open(
                          `/call/${lastCompletedCheckIn.source_conversation_id}`,
                          '_blank',
                        )
                      }
                      className="h-6 text-xs"
                    >
                      View
                    </Button>
                  </div>
                </div>
              )}

              {/* General Notes */}
              {lastCheckIn.general_notes && (
                <div className="px-2">
                  <p className="text-xs font-semibold text-gray-900 mb-1">
                    General Notes
                  </p>
                  <MarkdownText
                    content={lastCheckIn.general_notes}
                    className="text-xs text-gray-800"
                    plainTextClassName="italic"
                    markdownClassName="prose-xs text-gray-800 prose-p:my-0.5 prose-p:text-xs prose-ul:my-0.5 prose-ol:my-0.5 prose-li:my-0 prose-li:text-xs"
                  />
                </div>
              )}

              {/* Active Provider Updates */}
              {activeProviderDeltas.length > 0 && (
                <div className="px-2">
                  <p className="text-xs font-semibold text-gray-900 mb-1.5">
                    Provider Updates
                  </p>
                  <div className="space-y-1.5">
                    {activeProviderDeltas.map((providerDelta) => {
                      const newStatus = providerDelta.update
                        .new_treatment_status as ProviderTreatmentStatus | null;
                      const newStatusInfo = newStatus
                        ? treatmentStatusInfo[newStatus]
                        : null;
                      const Icon = newStatusInfo?.icon;

                      return (
                        <div
                          key={providerDelta.update.provider_name}
                          className="p-1.5 bg-gray-50 border border-gray-200 rounded text-xs"
                        >
                          <p className="font-semibold text-gray-900">
                            {providerDelta.update.provider_name}
                          </p>
                          {providerDelta.update.new_treatment_notes && (
                            <p className="text-gray-700 italic mt-0.5">
                              {providerDelta.update.new_treatment_notes}
                            </p>
                          )}
                          {newStatusInfo && Icon && (
                            <div className="flex items-center gap-1.5 mt-0.5">
                              <Icon
                                className={`h-3 w-3 ${newStatusInfo.color}`}
                              />
                              <span className="text-gray-700 text-[11px]">
                                Status:{' '}
                                <span className="font-medium">
                                  {newStatusInfo.label}
                                </span>
                              </span>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}
            </CollapsibleContent>
          </div>
        </Collapsible>

        {/* 2b. AI EXTRACTED DETAILS + COMPLETED PROVIDERS */}
        <Collapsible
          open={isCompletedOpen}
          onOpenChange={(open) => {
            setIsDetailsOpen(open);
            setIsCompletedOpen(open);
          }}
        >
          <div className="p-2 border border-gray-300 rounded-lg bg-white">
            <CollapsibleTrigger asChild>
              <Button
                variant="ghost"
                size="sm"
                className="w-full flex items-center justify-between hover:bg-gray-50 p-1.5"
              >
                <div className="flex items-center gap-2">
                  <Brain className="h-3.5 w-3.5 text-gray-600" />
                  <span className="text-xs font-medium text-gray-900">
                    AI-Extracted Details
                  </span>
                  <Badge
                    variant="outline"
                    className="text-[10px] text-gray-700 border-gray-300 px-1 py-0"
                  >
                    AI
                  </Badge>
                </div>
                <ChevronDown
                  className={`h-3.5 w-3.5 text-gray-600 transition-transform ${
                    isCompletedOpen ? 'rotate-180' : ''
                  }`}
                />
              </Button>
            </CollapsibleTrigger>

            <CollapsibleContent className="pt-2 space-y-2">
              {aiExtraction &&
                aiExtraction.extraction_status === 'completed' && (
                  <div className="px-2 space-y-1.5">
                    {/* Claimant Summary */}
                    {aiExtraction.how_claimant_feels && (
                      <div className="py-1 bg-gray-50 border border-gray-200 rounded px-2 text-xs space-y-0.5">
                        {aiExtraction.how_claimant_feels.summary && (
                          <p className="text-gray-800 italic">
                            {aiExtraction.how_claimant_feels.summary}
                          </p>
                        )}
                        {aiExtraction.how_claimant_feels.severity?.value !=
                          null && (
                          <p className="text-gray-900 font-semibold">
                            Pain Level:{' '}
                            {aiExtraction.how_claimant_feels.severity.value}/10
                            {aiExtraction.how_claimant_feels.severity
                              .descriptor &&
                              ` (${aiExtraction.how_claimant_feels.severity.descriptor})`}
                          </p>
                        )}
                        {aiExtraction.how_claimant_feels.symptoms_reported
                          .length > 0 && (
                          <p className="text-gray-700">
                            <span className="font-semibold">Symptoms:</span>{' '}
                            {aiExtraction.how_claimant_feels.symptoms_reported.join(
                              ', ',
                            )}
                          </p>
                        )}
                        {aiExtraction.how_claimant_feels.functional_impact
                          ?.reported &&
                          aiExtraction.how_claimant_feels.functional_impact
                            .details.length > 0 && (
                            <p className="text-gray-700">
                              <span className="font-semibold">
                                Limitations:
                              </span>{' '}
                              {aiExtraction.how_claimant_feels.functional_impact.details.join(
                                ', ',
                              )}
                            </p>
                          )}
                      </div>
                    )}

                    {/* AI-Extracted Provider Notes */}
                    {aiExtraction.provider_extractions.length > 0 && (
                      <div className="space-y-1">
                        {aiExtraction.provider_extractions.map(
                          (provider, idx) => (
                            <div
                              key={idx}
                              className="py-1 bg-gray-50 border border-gray-200 rounded px-2 text-xs"
                            >
                              <p className="font-semibold text-gray-900">
                                {provider.provider_name}
                                {provider.treatment_status && (
                                  <span className="font-normal text-gray-600">
                                    {' '}
                                    &middot; {provider.treatment_status}
                                  </span>
                                )}
                              </p>
                              {provider.treatment_notes && (
                                <p className="text-gray-700 italic mt-0.5">
                                  {provider.treatment_notes}
                                </p>
                              )}
                              {provider.treatment_frequency && (
                                <p className="text-gray-600 mt-0.5">
                                  Frequency: {provider.treatment_frequency}
                                </p>
                              )}
                              {provider.next_appointment?.scheduled && (
                                <p className="text-gray-600 mt-0.5">
                                  Next:{' '}
                                  {provider.next_appointment.date_or_timeframe}
                                  {provider.next_appointment.purpose &&
                                    ` (${provider.next_appointment.purpose})`}
                                </p>
                              )}
                            </div>
                          ),
                        )}
                      </div>
                    )}

                    {/* Treatment Completed */}
                    {aiExtraction.treatment_completed.reported &&
                      aiExtraction.treatment_completed.items.length > 0 && (
                        <div className="text-[11px] text-gray-800">
                          <span className="font-semibold">Completed:</span>{' '}
                          {aiExtraction.treatment_completed.items
                            .map((i) => i.type)
                            .join(', ')}
                        </div>
                      )}

                    {/* Treatment Ongoing */}
                    {aiExtraction.treatment_ongoing
                      .ongoing_treatment_reported &&
                      aiExtraction.treatment_ongoing.ongoing_items.length >
                        0 && (
                        <div className="text-[11px] text-gray-800">
                          <span className="font-semibold">Ongoing:</span>{' '}
                          {aiExtraction.treatment_ongoing.ongoing_items
                            .map((i) => i.type)
                            .join(', ')}
                        </div>
                      )}
                  </div>
                )}

              {/* Completed Providers Section */}
              {completedProviderDeltas.length > 0 && (
                <div className="px-2 pt-2 border-t border-gray-200">
                  <p className="text-xs font-semibold text-gray-900 mb-1.5 flex items-center gap-1.5">
                    <CheckCircle2 className="h-3.5 w-3.5 text-green-600" />
                    Completed Providers ({completedProviderDeltas.length})
                  </p>
                  <ul className="space-y-0.5">
                    {completedProviderDeltas.map((pd) => (
                      <li
                        key={pd.update.provider_name}
                        className="text-xs text-gray-600 flex items-center gap-1.5"
                      >
                        <CheckCircle2 className="h-3 w-3 text-green-600 flex-shrink-0" />
                        <span className="line-through">
                          {pd.update.provider_name}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </CollapsibleContent>
          </div>
        </Collapsible>
      </div>
    </div>
  );
};
